import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { comnUtils } from "@/comn/utils"; // 프로젝트 공통 유틸
import { Page, Group, Layout, Button, IconButton, Grid } from "@/comn/components"; // 화면 구성 컴포넌트
import { useFetch, useForm, useStore, useToast, useResource, useModal, usePage, useGrid } from "@/comn/hooks";
import {
    URLS,
    APIS,
    SF_CSTM_OFCR_INFO_FORM,
    SG_USR_CSTM_LIST,
} from "@/tra/tancis/ptli/usr/cstmofcr/services/PtliCstmOfcrService";

/*
 * Customs officer management details
 * !== 세관직원관리 상세 ==!
 */
export const PTLI0902004Q = () => {
    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    const { pgeUid } = usePage({
        auth: ["auth_ptli0015", "auth_ptlianyADM"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변화 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { pgeStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { usrDmnId } = useParams(); // Key Information of this Component Router !== 라우터에 정의된 키정보 ==!

    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    useResource({ defaultSchema: [] });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Customs officer management Form !== 세관직원관리 Form ==!
        cstmOfcrForm: useForm({
            defaultSchema: SF_CSTM_OFCR_INFO_FORM,
            defaultValues: { ...(pgeStore?.form || {}) },
        }),
    };

    /*
     * Declaration Grid
     * !== Grid 정의 ==!
     */
    const grid = { usrCstmList: useGrid({ defaultSchema: SG_USR_CSTM_LIST }) };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        getCstmOfcr: useFetch({
            api: (usrDmnId) => APIS.getCstmOfcr(usrDmnId),
            enabled: false,
            onSuccess: (data) => {
                let content = data.cstmOfcrInfo.content;
                if (!comnUtils.isEmpty(content)) {
                    form.cstmOfcrForm.setValues({
                        ...content,
                        fileGrpUuid: content.usrDmnId,
                        fileAtchGrpUuid: `${content.usrDmnId}-attach`,
                    });
                }
            },
            onError: (error) => {
                const errorData = error.response.data;

                if (error.response.status === 400) {
                    form.cstmOfcrForm.setErrors(errorData.errors);

                    modal.openModal({
                        content: "msg.00006", // An error occurred during processing.
                    });

                    return;
                }

                const { errorCode, errorMessage } = errorData;

                // BizException
                if (errorCode === "-999") {
                    modal.openModal({ content: errorMessage });
                } else {
                    toast.showToast({ type: "error", content: "msg.com.00018" }); // An error occurred. !== 에러가 발생했습니다! ==!
                }

                navigate(URLS.ptli0902001q);
            },
        }),
        getUsrCstmList: useFetch({
            api: (usrDmnId) => APIS.getUsrCstmList(usrDmnId),
            onSuccess: () => {},
            onError: () => {
                toast.showToast({ type: "error", content: "msg.com.00018" }); // An error occurred. !== 에러가 발생했습니다! ==!
                navigate(URLS.ptli0902001q);
            },
        }),
        // Update Customs officer !== 세관직원 수정 ==!
        updateCstmOfcr: useFetch({
            api: (data) => APIS.updateCstmOfcr({ ...data }),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.com.00062", // Successfully saved., !== 정상적으로 저장되었습니다. ==!
                    onConfirm: () => navigate(URLS.ptli0902004q),
                    close: false,
                });
            },
            onError: (error) => {
                const errorData = error.response.data;

                if (error.response.status === 400) {
                    form.cstmOfcrForm.setErrors(errorData.errors);
                }

                modal.openModal({ content: "msg.00006" }); // An error occurred during processing.
            },
        }),
        // delete Customs Officer !== 세관직원 삭제 ==!
        deleteCstmOfcr: useFetch({
            api: () => APIS.deleteCstmOfcr(usrDmnId),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.com.00060", // Successfully removed. !== 정상적으로 삭제되었습니다. ==!
                    onCancel: () => navigate(URLS.ptli0902001q),
                });
            },
            onError: () => {},
        }),
        // Send password reset email !== 비밀번호 초기화 이메일 발송 ==!
        sendEmail: useFetch({
            api: (data) => APIS.sendEmail({ ...data }),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.com.00128", // Mail sending has been completed. !== 메일 발송이 완료되었습니다. ==!
                    onConfirm: () => {},
                    close: false,
                });
            },
            onError: (error) => {
                const errorData = error.response.data;

                if (error.response.status === 400) {
                    form.cstmOfcrForm.setErrors(errorData.errors);
                }

                modal.openModal({ content: "msg.00006" }); // An error occurred during processing.
            },
        }),

        getInternalUserReport: useFetch({
            api: (usrDmnId: string) => {
                return APIS.getInternalUserReport(usrDmnId);
            },
            onSuccess: (data) => {
                // API 조회 성공 후 모달창으로 Pdf 표시
                modal.openModal({
                    type: "pdf",
                    data: data,
                    thumbnail: true,
                    download: true,
                    print: true,
                });
            },
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Delete Customs Officer !== 세관직원 삭제 ==!
        onDeleteBtnClick: () => {
            modal.openModal({
                content: "msg.com.00049", // Confirm remove? !== 삭제하시겠습니까? ==!
                onConfirm: () => fetch.deleteCstmOfcr.fetch(),
            });
        },
        onEditBtnClick: () => navigate(`${URLS.ptli0902003s}/${usrDmnId}`),
        // Send password reset email !== 비밀번호 초기화 메일 발송 ==!
        onEmlBtnClick: () => {
            modal.openModal({
                content: "msg.ptl.00749", // Would you like to reset your password? !== 비밀번호를 초기화 하시겠습니까? ==!
                onConfirm: () => fetch.sendEmail.fetch(form.cstmOfcrForm.getValues()),
            });
        },
        onPrintBtnClick: () => {
            console.log(usrDmnId);
            fetch.getInternalUserReport.fetch(usrDmnId);
        },
    };

    useEffect(() => {
        form.cstmOfcrForm.setEditable(false);
        fetch.getCstmOfcr.fetch(usrDmnId);
        fetch.getUsrCstmList.fetch(usrDmnId);
    }, []);

    // @ts-ignore
    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Title title={"T_CSTM_OFCR"} titleSize={2} />
                        <Group.Row>
                            <Group.Control {...form.cstmOfcrForm.schema.cstmOfcrNo} />
                            <Group.Control {...form.cstmOfcrForm.schema.usrTin} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.cstmOfcrForm.schema.usrDmnId} />
                            <Group.Control {...form.cstmOfcrForm.schema.usrNm} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.cstmOfcrForm.schema.usrTelno} />
                            <Group.Control {...form.cstmOfcrForm.schema.usrEmlAddr} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.cstmOfcrForm.schema.actnUsrYn} />
                            <Group.Control {...form.cstmOfcrForm.schema.usrStatCdNm} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.cstmOfcrForm.schema.fileGrpUuid} copy={false} />
                            <Group.Control {...form.cstmOfcrForm.schema.fileAtchGrpUuid} copy={false} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.cstmOfcrForm.schema.mdfyRsn} />
                        </Group.Row>
                    </Group.Section>
                    <Group.Section>
                        <Group.Title title={"T_AFFIL"} titleSize={2}></Group.Title>
                        <Group.Row>
                            <Group.Control {...form.cstmOfcrForm.schema.cstmSctnOrgzCdNm} />
                            <Group.Control {...form.cstmOfcrForm.schema.cstmOfceCdNm} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.cstmOfcrForm.schema.cstmUnitOrgzCdNm} />
                            <Group.Control {...form.cstmOfcrForm.schema.roleNm} />
                        </Group.Row>
                    </Group.Section>

                    <Group.Section>
                        <Group.Title title={"T_SMS_EML_RCPN_YN"} titleSize={2}></Group.Title>
                        <Group.Row>
                            <Group.Control {...form.cstmOfcrForm.schema.smsRcpnYn} />
                            <Group.Control {...form.cstmOfcrForm.schema.emlRcpnYn} />
                        </Group.Row>
                    </Group.Section>

                    <Group.Section>
                        <Group.Title title={"T_USR_CSTM_LIST"} titleSize={2}></Group.Title>
                        <Grid {...grid.usrCstmList.grid} data={fetch.getUsrCstmList.data?.usrCstmList} />
                    </Group.Section>
                </Group.Body>
            </Group>

            <Layout direction="row">
                <Layout.Left>
                    <Button role="list" onClick={() => navigate(URLS.ptli0902001q)} />
                </Layout.Left>
                <Layout.Right>
                    <Layout.Button onClick={handler.onPrintBtnClick}>View PDF</Layout.Button>
                    <Layout.Button onClick={handler.onEmlBtnClick}>{t("B_PWD_INIT")}</Layout.Button>
                    <Layout.Button role={"edit"} onClick={handler.onEditBtnClick} />
                    <Layout.Button role={"delete"} onClick={handler.onDeleteBtnClick} />
                </Layout.Right>
            </Layout>
        </Page>
    );
};
